import { useEffect, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { Link } from "wouter";
import { FileUp } from "lucide-react";
import { paths, type IntakeItem } from "@clevis/shared-types";
import { Card, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call, CallError } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { QueueCard, type IntakeItemRow, type QueueCardResult } from "../../components/IntakeQueueCard";

// Work-order upload: drop a builder's document (PDF, or the email saved as
// .eml) and it goes through the same smart intake as monitored mail
// (functions intake/upload.ts): Clevis reads it, works out what kind of
// document it is, matches it to jobs you already have (PO, address, lot) and
// proposes what to do. You check the proposal here and apply it; nothing
// changes until you do. Your uploads stay here until applied or dismissed.

const MAX_BYTES = 6 * 1024 * 1024;

interface Finished {
  itemId: string;
  subject: string;
  result: QueueCardResult;
}

async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function describe(err: unknown): string {
  // Conflict / invalid-input hints from the server are the specific message.
  if (err instanceof CallError && err.hint && (err.code === "conflict" || err.code === "input.invalid")) return err.hint;
  return (err as Error).message;
}

export function WorkOrdersPage() {
  const { tenantId, user } = useAuth();
  const uid = user?.uid;
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [reading, setReading] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [items, setItems] = useState<IntakeItemRow[]>([]);
  const [finished, setFinished] = useState<Finished[]>([]);

  // My uploads still waiting for review (rules let a manager read only their own).
  useEffect(() => {
    if (!tenantId || !uid) return;
    const q = query(
      collection(db, paths.tenantIntakeItems(tenantId)),
      where("uploadedByUid", "==", uid),
      where("status", "==", "pending"),
    );
    return onSnapshot(
      q,
      (snap) =>
        setItems(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as IntakeItem) }))
            .sort((a, b) => b.createdAt - a.createdAt),
        ),
      (err) => setErrors([err.message]),
    );
  }, [tenantId, uid]);

  async function upload(files: File[]) {
    if (!tenantId || files.length === 0) return;
    const problems: string[] = [];
    setErrors([]);
    for (const [i, file] of files.entries()) {
      const isEml = /\.eml$/i.test(file.name) || file.type === "message/rfc822";
      const isPdf = /\.pdf$/i.test(file.name) || file.type === "application/pdf";
      if (!isEml && !isPdf) {
        problems.push(`${file.name}: upload a PDF or an email saved as .eml.`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        problems.push(`${file.name}: over 6 MB. Export a smaller copy and try again.`);
        continue;
      }
      setReading(files.length > 1 ? `Reading ${i + 1} of ${files.length}: ${file.name}…` : `Reading ${file.name}…`);
      try {
        const data = await toBase64(file);
        await call("queueUploadedWorkOrder", {
          tenantId,
          fileName: file.name,
          ...(isEml ? { emlBase64: data } : { pdfBase64: data }),
        });
      } catch (err) {
        problems.push(`${file.name}: ${describe(err)}`);
      }
    }
    setReading(null);
    setErrors(problems);
    if (fileInput.current) fileInput.current.value = "";
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <h1 className="text-lg font-bold">Work orders</h1>
      <p className="text-xs text-steel">
        Drop a builder's purchase order, start notice, completion notice, schedule change or cancellation (PDF, or the
        email saved as .eml). Clevis reads it, matches it to the jobs you already have, and proposes what to do. Check
        the proposal below and apply it. Nothing changes until you do.
      </p>
      {errors.map((e, i) => (
        <p key={i} className="text-xs text-danger">
          {e}
        </p>
      ))}

      <Card
        onDragOver={(e) => {
          e.preventDefault();
          if (!reading) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!reading) void upload(Array.from(e.dataTransfer.files));
        }}
      >
        <button
          className={cn(
            "flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-steel/40 py-8 text-steel hover:border-brandorange hover:text-brandorange-dark",
            dragging && "border-brandorange text-brandorange-dark",
          )}
          onClick={() => fileInput.current?.click()}
          disabled={!!reading}
        >
          <FileUp size={22} />
          <span className="text-sm">{reading ?? "Upload or drop a work order (PDF or .eml email)"}</span>
          {reading ? <span className="text-xs">Matching it to your jobs takes up to a minute.</span> : null}
        </button>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept="application/pdf,.pdf,.eml,message/rfc822"
          className="hidden"
          onChange={(e) => void upload(Array.from(e.target.files ?? []))}
        />
      </Card>

      {finished.map((f) => (
        <Card key={f.itemId} className="py-2 text-xs text-ink">
          {f.result.status === "dismissed" ? (
            <span className="text-steel">Dismissed “{f.subject}”. Nothing was changed.</span>
          ) : (
            <>
              <span className="font-semibold">Applied “{f.subject}”:</span>
              {f.result.applied.map((a) => (
                <span key={a.actionId} className="ml-2">
                  {a.detail}
                  {a.jobId ? (
                    <Link href={`/jobs/${a.jobId}`} className="ml-1 text-brandorange-dark underline">
                      open job
                    </Link>
                  ) : null}
                </span>
              ))}
            </>
          )}
        </Card>
      ))}

      {items.length > 0 ? (
        <h2 className="text-sm font-semibold text-ink">
          {items.length === 1 ? "1 upload waiting for your review" : `${items.length} uploads waiting for your review`}
        </h2>
      ) : null}
      {items.map((item) => (
        <QueueCard
          key={item.id}
          item={item}
          onResolved={(result) =>
            setFinished((fs) => [{ itemId: item.id, subject: item.source.subject, result }, ...fs].slice(0, 5))
          }
        />
      ))}
    </div>
  );
}
